import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { RegisterFormData } from '../../types';
import { Eye, EyeOff } from 'lucide-react';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import LinkedInButton from '../../components/auth/LinkedInButton';
import GoogleButton from '../../components/auth/GoogleButton';
import { writeLegalConsent } from '../../utils/legalConsent';

const registerSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Confirm password is required'),
  termsAccepted: z.boolean().refine((v) => v === true, {
    message: 'You must accept the Terms of Service',
  }),
  privacyAccepted: z.boolean().refine((v) => v === true, {
    message: 'You must accept the Privacy Policy',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

const Register: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser } = useAuth();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      termsAccepted: false,
      privacyAccepted: false,
    },
  });

  const termsAccepted = watch('termsAccepted');
  const privacyAccepted = watch('privacyAccepted');
  const legalReady = !!termsAccepted && !!privacyAccepted;

  const persistLegalConsent = () => {
    writeLegalConsent({ termsAccepted: true, privacyAccepted: true });
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      persistLegalConsent();
      const success = await registerUser(data.email, data.password);

      if (!success) {
        setError('Registration failed. Please try again.');
      }
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'An error occurred during registration. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-3xl font-extrabold text-gray-900">Create a new account</h2>
        </motion.div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="py-8 px-4 sm:px-10 relative">
            {isLoading && <LoadingOverlay />}

            {error && (
              <div className="mb-4 p-3 bg-error-50 text-error-600 text-sm rounded-md border border-error-200">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Input
                id="email"
                type="email"
                label="Email address"
                autoComplete="email"
                error={errors.email?.message}
                {...register('email')}
              />

              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  autoComplete="new-password"
                  error={errors.password?.message}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-9 text-gray-500 focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  autoComplete="new-password"
                  error={errors.confirmPassword?.message}
                  {...register('confirmPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-9 text-gray-500 focus:outline-none"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="mt-1" {...register('termsAccepted')} />
                  <span>
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" className="font-medium text-primary-700 hover:text-primary-800">
                      Terms of Service
                    </Link>
                  </span>
                </label>
                {errors.termsAccepted && <p className="text-sm text-error-600">{errors.termsAccepted.message}</p>}
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="mt-1" {...register('privacyAccepted')} />
                  <span>
                    I agree to the{' '}
                    <Link to="/privacy" target="_blank" className="font-medium text-primary-700 hover:text-primary-800">
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                {errors.privacyAccepted && <p className="text-sm text-error-600">{errors.privacyAccepted.message}</p>}
                <p className="text-xs text-gray-500">By signing up, you also agree to receive emails from IDCoder.</p>
              </div>

              <div>
                <Button type="submit" fullWidth isLoading={isLoading}>
                  Create Account
                </Button>
              </div>
              <div className="my-6">
                <div className="flex items-center">
                  <div className="flex-grow border-t border-gray-200" />
                  <span className="mx-2 text-gray-400 text-sm">OR</span>
                  <div className="flex-grow border-t border-gray-200" />
                </div>
              </div>
              {!legalReady && (
                <p className="text-xs text-center text-gray-500">Accept Terms and Privacy Policy to continue with LinkedIn or Google.</p>
              )}
              <div
                onClick={() => {
                  if (legalReady) persistLegalConsent();
                }}
              >
                <LinkedInButton disabled={isLoading || !legalReady} onError={setError} />
              </div>
              <div
                className="mt-3"
                onClick={() => {
                  if (legalReady) persistLegalConsent();
                }}
              >
                <GoogleButton disabled={isLoading || !legalReady} onError={setError} />
              </div>
              <div className="mt-4">
                <Link to="/login">
                  <Button variant="outline" fullWidth type="button">
                    Sign in to your existing account
                  </Button>
                </Link>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
